import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Flame, Play, Sparkles, TrendingUp, Dumbbell, Timer, Zap, Crown } from "lucide-react";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);

  useEffect(() => {
    api.get("/stats/summary").then((r) => setStats(r.data));
    api.get("/workouts").then((r) => setWorkouts(r.data));
    api.get("/payments/config").then((r) => setPaymentsEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const featured = workouts[0];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-2 text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB]">Ciao {user?.name}</div>
      <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9] mb-8">
        Il tuo <span className="text-[#FF3B30]">comando</span>
      </h1>

      {paymentsEnabled && (
        <Link
          to="/subscribe"
          data-testid="pro-cta"
          className="mb-6 border border-[#E2FF31]/40 bg-gradient-to-r from-[#E2FF31]/10 to-transparent p-4 flex items-center justify-between gap-4 hover:border-[#E2FF31] transition-all group"
        >
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-[#E2FF31]" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#E2FF31]">FitCommand Pro</div>
              <div className="text-sm font-bold group-hover:text-[#E2FF31]">Sblocca AI Coach illimitato e programmi settimanali</div>
            </div>
          </div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#E2FF31]">Scopri →</div>
        </Link>
      )}

      {/* Featured workout banner */}
      <div className="relative overflow-hidden border border-[#2A2A35] mb-8 min-h-[280px] flex" data-testid="today-banner">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/14898419/pexels-photo-14898419.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0F] via-[#0B0B0F]/85 to-[#0B0B0F]/20" />
        </div>
        <div className="relative z-10 p-8 sm:p-12 flex flex-col justify-between w-full">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E2FF31] mb-3 flex items-center gap-2">
              <Flame className="w-3 h-3" /> Allenamento di oggi
            </div>
            <div className="font-display text-4xl sm:text-5xl font-black uppercase leading-[0.95] tracking-tight max-w-xl">
              {featured ? featured.title : "Nessuna scheda ancora"}
            </div>
            {featured && (
              <div className="mt-3 flex items-center gap-4 text-sm text-[#A0A0AB]">
                <span className="flex items-center gap-1"><Timer className="w-4 h-4" /> {featured.duration_min} min</span>
                <span className="flex items-center gap-1"><Dumbbell className="w-4 h-4" /> {featured.exercises.length} esercizi</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            {featured ? (
              <button
                onClick={() => navigate(`/session/${featured.id}`)}
                data-testid="start-workout-btn"
                className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#e02e24] transition-all flex items-center gap-2 pulse-accent"
              >
                <Play className="w-4 h-4 fill-white" /> Inizia ora
              </button>
            ) : (
              <Link
                to="/coach"
                data-testid="generate-first-btn"
                className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#e02e24] transition-all flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> Genera scheda IA
              </Link>
            )}
            <Link
              to="/workouts"
              data-testid="see-all-workouts"
              className="border border-white/40 text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-white hover:text-black transition-all"
            >
              Tutte le schede
            </Link>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 stagger">
        <StatBox
          label="Striscia"
          value={stats?.streak ?? 0}
          suffix="gg"
          accent="#FF3B30"
          icon={Flame}
          testid="stat-streak"
        />
        <StatBox
          label="Allenamenti"
          value={stats?.total_workouts ?? 0}
          suffix=""
          accent="#E2FF31"
          icon={Zap}
          testid="stat-workouts"
        />
        <StatBox
          label="Minuti totali"
          value={stats?.total_minutes ?? 0}
          suffix="min"
          accent="#FFFFFF"
          icon={Timer}
          testid="stat-minutes"
        />
        <StatBox
          label="Kcal stimate"
          value={stats?.total_calories ?? 0}
          suffix="kcal"
          accent="#A0A0AB"
          icon={TrendingUp}
          testid="stat-calories"
        />
      </div>

      {/* Weekly grid */}
      <div className="border border-[#2A2A35] p-6 mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-4">Ultimi 7 giorni</div>
        <div className="grid grid-cols-7 gap-2">
          {(stats?.last_7_days || []).map((d, i) => {
            const active = d.count > 0;
            const date = new Date(d.date);
            const dayLabel = DAYS[(date.getDay() + 6) % 7];
            return (
              <div key={d.date} className="flex flex-col items-center gap-2" data-testid={`weekday-${i}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">{dayLabel}</div>
                <div
                  className={`w-full aspect-square flex items-center justify-center border transition-all ${
                    active
                      ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                      : "border-[#2A2A35] text-[#A0A0AB]"
                  }`}
                >
                  <span className="font-display text-2xl font-black">{date.getDate()}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent workouts */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight">Le tue schede</h2>
          <Link to="/coach" className="text-xs uppercase tracking-widest text-[#FF3B30] hover:underline" data-testid="quick-coach">
            + Nuova con IA
          </Link>
        </div>
        {workouts.length === 0 ? (
          <div className="border border-[#2A2A35] p-8 text-center text-[#A0A0AB]">
            Ancora nessuna scheda. Chiedi al <Link to="/coach" className="text-[#FF3B30] underline">Coach IA</Link> di generarne una.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
            {workouts.slice(0, 6).map((w) => (
              <Link
                key={w.id}
                to={`/workouts/${w.id}`}
                data-testid={`workout-card-${w.id}`}
                className="border border-[#2A2A35] p-5 hover:border-[#FF3B30] hover:-translate-y-1 transition-all block group"
              >
                <div className="flex items-center gap-2 mb-2">
                  {w.ai_generated && <span className="text-[9px] uppercase tracking-widest text-[#E2FF31] flex items-center gap-1"><Sparkles className="w-3 h-3" />IA</span>}
                  <span className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">{w.focus}</span>
                </div>
                <div className="font-display text-xl font-bold uppercase leading-tight mb-3 group-hover:text-[#FF3B30] transition-colors">
                  {w.title}
                </div>
                <div className="text-xs text-[#A0A0AB] flex items-center gap-3">
                  <span>{w.duration_min} min</span>
                  <span>·</span>
                  <span>{w.exercises.length} esercizi</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, suffix, accent, icon: Icon, testid }) {
  return (
    <div className="border border-[#2A2A35] p-5 relative overflow-hidden" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB]">{label}</div>
        <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={2.5} />
      </div>
      <div className="font-display text-5xl sm:text-6xl font-black tracking-tighter" style={{ color: accent }}>
        {value}
      </div>
      {suffix && <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mt-1">{suffix}</div>}
    </div>
  );
}
